import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";

import { NetworkRow, useWallet } from "@/entities/wallet";
import { ROUTES } from "@/shared/config";
import { Button } from "@/shared/ui";
import { ScreenSkeleton } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Шаг 2 вывода: сеть и адрес. Каркас `Select Network` (23:122) плюс
 * `NetworkRow` (17:64) — DESIGN.md §5.
 *
 * Списка сетей для вывода бэкенд не отдаёт и `asset`/`network` не валидирует:
 * `deposit_assets` описывает только пополнение. Берём тот же список — других
 * реквизитов у проекта нет, и предлагать пользователю сеть, в которую сам
 * проект не умеет, было бы хуже (DESIGN.md §8).
 */
export function WithdrawAddressPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as { amount?: string } | null;
  const [asset, setAsset] = useState<string | null>(null);
  const [network, setNetwork] = useState<string | null>(null);
  const [address, setAddress] = useState("");

  const { data, isPending } = useWallet();

  if (!state?.amount) return <Navigate to={ROUTES.withdrawAmount} replace />;
  if (isPending) return <ScreenSkeleton blocks={[88, 88, 80]} />;

  const assets = Object.entries(data?.deposit_assets ?? {});
  // Единственный актив выбирать не из чего — он подставляется сам.
  const currentAsset = asset ?? (assets.length === 1 ? (assets[0]?.[0] ?? null) : null);
  const networks = assets.find(([name]) => name === currentAsset)?.[1] ?? [];

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("withdraw.addressTitle")} subtitle={t("withdraw.addressSubtitle")} />

      {assets.length > 1 ? (
        <div role="group" aria-label={t("withdraw.selectAsset")} className="flex gap-2">
          {assets.map(([name]) => (
            <button
              key={name}
              type="button"
              aria-pressed={currentAsset === name}
              data-haptic="select"
              onClick={() => {
                setAsset(name);
                setNetwork(null);
              }}
              className={[
                "press rounded-xl border px-4 py-2.5 text-label font-semibold",
                "transition-colors duration-[var(--motion-fast)] ease-standard",
                currentAsset === name
                  ? "border-action-primary bg-surface-selected text-text-primary"
                  : "border-border-subtle text-text-tertiary",
              ].join(" ")}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}

      <p className="rounded-2xl border border-border-subtle bg-background-elevated p-4 text-caption font-normal text-text-secondary">
        {t("withdraw.addressWarning")}
      </p>

      <ul className="flex flex-col gap-3">
        {networks.map((item) => (
          <li key={item}>
            <NetworkRow network={item} selected={network === item} onSelect={() => setNetwork(item)} />
          </li>
        ))}
      </ul>

      <label className="flex flex-col gap-2">
        <span className="text-caption font-medium text-text-secondary">{t("withdraw.addressLabel")}</span>
        <input
          value={address}
          onChange={(event) => setAddress(event.target.value.trim())}
          placeholder={t("withdraw.addressPlaceholder")}
          className="rounded-xl border border-border-subtle bg-background-raised px-3 py-3 font-mono text-caption text-text-primary outline-none placeholder:text-text-tertiary"
        />
      </label>

      <Button
        size="cta"
        block
        disabled={currentAsset === null || network === null || address.length < 4}
        onClick={() =>
          void navigate(ROUTES.withdrawConfirm, {
            state: { amount: state.amount, asset: currentAsset, network, address },
          })
        }
      >
        {t("common.next")}
      </Button>
    </div>
  );
}
